/**
 * @deprecated Use staging environment (VITE_APP_ENV=staging) instead.
 * DEMO_MODE bypasses real auth and database — it is NOT suitable for pre-production testing.
 * This file is preserved for investor demos only. Do not use for QA/testing.
 * See src/lib/env.ts for the staging environment system.
 *
 * DEMO MODE (legacy)
 * When true: all flows run with simulated backend responses.
 * UI is identical to production — no steps are skipped.
 * Safe for investor demos without real credentials.
 *
 * BUNDLE HYGIENE: every check below reads import.meta.env.VITE_APP_ENV
 * *inline* (not via a shared const) so Vite's static replacement turns it
 * into a literal and minification drops the demo-only branches — including
 * the persona objects — from non-demo builds. scripts/assert-no-demo-strings.mjs
 * asserts the demo emails are absent from the production bundle; keep it in
 * sync if personas change.
 */

/**
 * True ONLY in the investor demo environment (`VITE_APP_ENV=demo`, i.e.
 * `npm run dev:demo` / `npm run build:demo`). In every deployed environment
 * (staging, production, UAT) Vite inlines the comparison to `false` at build
 * time, so all `if (DEMO_MODE)` branches are dead code the bundler strips —
 * they carry zero runtime risk but must be preserved for investor demos.
 *
 * Rules (issue #297 / TH-063):
 * - NEVER assign a literal here (`= true`) — scripts/assert-no-demo-strings.mjs
 *   fails the build if the env comparison is replaced with a literal.
 * - Do NOT use DEMO_MODE for QA/testing toggles — use the staging env
 *   (VITE_APP_ENV=staging, src/lib/env.ts + StagingBanner) instead.
 * - New demo fixtures belong behind inline `import.meta.env.VITE_APP_ENV
 *   === 'demo'` ternaries (see src/data/demoData.ts) so they fold out of
 *   non-demo bundles.
 */
export const DEMO_MODE = import.meta.env.VITE_APP_ENV === 'demo';

/** Simulate a network delay (300-800ms) */
export const demoDelay = (ms = 500) =>
  new Promise<void>(res => setTimeout(res, ms + Math.random() * 300));

/** Generate a fake booking ID for demo */
export const fakeDemoBookingId = () =>
  'demo-' + Math.random().toString(36).slice(2, 10).toUpperCase();

/** Fake transaction ref */
export const fakeTxRef = (bookingId: string) =>
  `th_${bookingId}_${Date.now()}`;

interface DemoPersona {
  id: string;
  email: string;
  app_metadata: { provider: string; providers: string[] };
  user_metadata: { first_name: string; last_name: string; full_name: string };
  aud: string;
  created_at: string;
  email_confirmed_at: string;
  phone: string;
  confirmed_at: string;
  last_sign_in_at: string;
  role: string;
  updated_at: string;
}

/** Same shape as a real persona so unguarded property access never crashes
 *  in non-demo builds; carries no demo PII. */
const emptyPersona = (id: string): DemoPersona => ({
  id,
  email: '',
  app_metadata: { provider: 'email', providers: ['email'] },
  user_metadata: { first_name: '', last_name: '', full_name: '' },
  aud: 'authenticated',
  created_at: '',
  email_confirmed_at: '',
  phone: '',
  confirmed_at: '',
  last_sign_in_at: '',
  role: 'authenticated',
  updated_at: '',
});

// Demo client user
export const DEMO_USER: DemoPersona = import.meta.env.VITE_APP_ENV === 'demo'
  ? {
      id: 'demo-user-123',
      email: 'demo@trustedhelpers.ug',
      app_metadata: { provider: 'email', providers: ['email'] },
      user_metadata: {
        first_name: 'Sarah',
        last_name: 'Nakato',
        full_name: 'Sarah Nakato',
      },
      aud: 'authenticated',
      created_at: '2026-01-01T00:00:00.000Z',
      email_confirmed_at: '2026-01-01T00:00:00.000Z',
      phone: '+256 700 000 001',
      confirmed_at: '2026-01-01T00:00:00.000Z',
      last_sign_in_at: new Date().toISOString(),
      role: 'authenticated',
      updated_at: new Date().toISOString(),
    }
  : emptyPersona('demo-user-123');

// Demo helper user
export const DEMO_HELPER: DemoPersona = import.meta.env.VITE_APP_ENV === 'demo'
  ? {
      id: 'demo-helper-456',
      email: 'grace.atim@trustedhelpers.ug',
      app_metadata: { provider: 'email', providers: ['email'] },
      user_metadata: {
        first_name: 'Grace',
        last_name: 'Atim',
        full_name: 'Grace Atim',
      },
      aud: 'authenticated',
      created_at: '2026-01-15T00:00:00.000Z',
      email_confirmed_at: '2026-01-15T00:00:00.000Z',
      phone: '+256 701 234 567',
      confirmed_at: '2026-01-15T00:00:00.000Z',
      last_sign_in_at: new Date().toISOString(),
      role: 'authenticated',
      updated_at: new Date().toISOString(),
    }
  : emptyPersona('demo-helper-456');

// Demo admin user
export const DEMO_ADMIN: DemoPersona = import.meta.env.VITE_APP_ENV === 'demo'
  ? {
      id: 'demo-admin-789',
      email: 'admin@trustedhelpers.com',
      app_metadata: { provider: 'email', providers: ['email'] },
      user_metadata: {
        first_name: 'Admin',
        last_name: 'Manager',
        full_name: 'Admin Manager',
      },
      aud: 'authenticated',
      created_at: '2024-01-01T00:00:00.000Z',
      email_confirmed_at: '2024-01-01T00:00:00.000Z',
      phone: '+256702345678',
      confirmed_at: '2024-01-01T00:00:00.000Z',
      last_sign_in_at: new Date().toISOString(),
      role: 'authenticated',
      updated_at: new Date().toISOString(),
    }
  : emptyPersona('demo-admin-789');

// Get current demo role from localStorage
export function getDemoRole(): 'client' | 'helper' | 'admin' {
  try {
    const stored = localStorage.getItem('trustedHelpers_demoRole');
    if (stored === 'helper' || stored === 'admin' || stored === 'client') return stored;
  } catch (_) {
    /* ignore: localStorage unavailable (private mode / SSR) — fall back to default */
  }
  return 'client';
}

// Set current demo role in localStorage
export function setDemoRole(role: 'client' | 'helper' | 'admin'): void {
  try {
    localStorage.setItem('trustedHelpers_demoRole', role);
  } catch (_) {
    /* ignore: best-effort persistence — localStorage may be unavailable */
  }
}
